import { useState } from "react";
import api from "../../../lib/api";
import { card, btn, btnGhost, label } from "./_genCommon";

const KEYS_PER_DOMAIN = {
  real_estate: ["form", "structure", "facade", "hvac", "parking", "formwork"],
  infrastructure: ["alignment_strategy", "pavement", "median", "bridges_per_crossing"],
  process_plant: ["cooling_system", "column_type", "heat_integration", "layout",
                          "material_philosophy", "redundancy", "energy_recovery",
                          "pump_type", "control"],
};

/** Build a hybrid alt by picking which keys to take from which alt. */
export default function HybridBuilder({ projectId, alts, onApplied, onClose }) {
  const domain = alts[0]?.domain || "real_estate";
  const keys = KEYS_PER_DOMAIN[domain] || [];
  // For each key → which alt_id provides it (default: top-ranked alt).
  const [picks, setPicks] = useState(
    Object.fromEntries(keys.map(k => [k, alts[0]?.id]))
  );
  const [name, setName] = useState("Custom Hybrid");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const apply = async () => {
    setBusy(true); setErr("");
    try {
      // Group by alt_id
      const sources = {};
      Object.entries(picks).forEach(([k, alt_id]) => {
        if (!alt_id) return;
        sources[alt_id] = sources[alt_id] || [];
        sources[alt_id].push(k);
      });
      const body = {
        name,
        sources: Object.entries(sources).map(([alt_id, ks]) =>
            ({ alt_id, keys: ks })),
      };
      const r = await api.post(`/projects/${projectId}/generative/hybrid`, body);
      onApplied?.(r.data);
      onClose?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
    }
    setBusy(false);
  };

  return (
    <div data-testid="gen-hybrid" style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
        zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 32 }}>
      <div style={{ ...card, padding: 24, maxWidth: 800, width: "100%",
                          maxHeight: "80vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#F26B1F" }}>
              Mix &amp; Match — Hybrid Alternative
            </div>
            <div style={{ fontSize: 11, color: "var(--pf-muted)", marginTop: 2 }}>
              Pick the best feature from each alt — Capexara Process Industry rebuilds the design.
            </div>
          </div>
          <button data-testid="gen-hybrid-close" style={btnGhost} onClick={onClose}>
            ✕ Close
          </button>
        </div>
        <div style={{ marginTop: 16 }}>
          <div style={label}>Hybrid name</div>
          <input data-testid="gen-hybrid-name"
              value={name} onChange={(e) => setName(e.target.value)}
              style={{ ...btnGhost, fontSize: 12, padding: 8, marginTop: 6,
                          width: "100%", textAlign: "left" }} />
        </div>
        <div style={{ marginTop: 16 }}>
          <div style={label}>Pick source alt for each parameter</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                              gap: 8, marginTop: 8 }}>
            {keys.map(k => (
              <div key={k} style={{ background: "var(--pf-bg)",
                                                border: "1px solid var(--pf-border)",
                                                borderRadius: 4, padding: 10 }}>
                <div style={{ fontSize: 10, color: "var(--pf-muted)" }}>
                  {k.replace(/_/g, " ").toUpperCase()}
                </div>
                <select data-testid={`gen-hybrid-pick-${k}`}
                    value={picks[k] || ""}
                    onChange={(e) => setPicks(p => ({ ...p, [k]: e.target.value }))}
                    style={{ ...btnGhost, width: "100%", fontSize: 11,
                                padding: 6, marginTop: 6 }}>
                  {alts.map(a => {
                    const val = a.variations?.[k];
                    const desc = typeof val === "object" && val !== null
                                      ? (val.type || val.system || JSON.stringify(val).slice(0, 30))
                                      : (val || "—");
                    return (
                      <option key={a.id} value={a.id}>
                        {a.id} · {desc}
                      </option>
                    );
                  })}
                </select>
              </div>
            ))}
          </div>
        </div>
        {err && <div style={{ marginTop: 12, color: "#FF8888", fontSize: 11 }}>{err}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end",
                            gap: 8, marginTop: 16 }}>
          <button data-testid="gen-hybrid-cancel" style={btnGhost}
              onClick={onClose}>Cancel</button>
          <button data-testid="gen-hybrid-apply" style={btn}
              onClick={apply} disabled={busy}>
            {busy ? "Building…" : "Build Hybrid →"}
          </button>
        </div>
      </div>
    </div>
  );
}
